/**
 * "Why this customer": which accounts the earlier quotations, order confirmations and
 * rep confirmations of this sender (and of its mail domain) went to.
 *
 * Read from the app database when the rep opens the disclosure. Reps and managers see
 * the same; no price or cost is part of it.
 *
 * Kept free of React so the request shapes and the wording can be tested.
 */

import { type ApiError, client } from "./client";
import type { ContactMemoryAccount, ContactMemorySources, ContactMemoryView } from "./live-types";

export const customerMemoryKey = (caseId: string) => ["case-customer-memory", caseId];

/** The memory of the case's sender; null when the route does not exist (older backend). */
export async function getCustomerMemory(caseId: string): Promise<ContactMemoryView | null> {
  try {
    const { data } = await client.get<{ 200: ContactMemoryView }, unknown, true>({
      url: "/cases/{case_id}/customer/memory",
      path: { case_id: caseId },
      throwOnError: true,
    });
    return data && typeof data === "object" ? data : null;
  } catch (error) {
    if ((error as ApiError | null)?.status === 404) {
      return null;
    }
    throw error;
  }
}

function count(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "2 sent documents, 1 quotation in the order system, 1 confirmed by sales"; "" without any. */
export function sourcesSentence(sources: ContactMemorySources | null | undefined): string {
  const parts: string[] = [];
  const sent = count(sources?.sent);
  const quotes = count(sources?.quote_hdr);
  const outcome = count(sources?.outcome);
  const rep = count(sources?.rep);
  if (sent > 0) {
    parts.push(plural(sent, "sent document", "sent documents"));
  }
  if (quotes > 0) {
    parts.push(plural(quotes, "quotation in the order system", "quotations in the order system"));
  }
  if (outcome > 0) {
    parts.push(plural(outcome, "reply read by the workbench", "replies read by the workbench"));
  }
  if (rep > 0) {
    parts.push(plural(rep, "confirmed by sales", "confirmed by sales"));
  }
  return parts.join(", ");
}

/** Usable accounts, most documents first. */
export function memoryAccounts(raw: unknown): ContactMemoryAccount[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(
      (entry): entry is ContactMemoryAccount =>
        entry != null &&
        typeof entry === "object" &&
        typeof (entry as ContactMemoryAccount).account === "string" &&
        (entry as ContactMemoryAccount).account.trim() !== "",
    )
    .sort((a, b) => count(b.documents) - count(a.documents));
}

/** Decision D5: the lead sentence of a sender with the multi-account mark. */
export const MULTI_ACCOUNT_LEAD =
  "This sender buys for more than one customer account. I2Q does not use the sender alone to find the customer.";

/** True when the sender carries a live multi-account mark that new drafts use. */
export function multiAccountActive(view: ContactMemoryView): boolean {
  return view.multi_account?.marked === true && view.multi_account?.active === true;
}

/** The sentence over the list. */
export function memoryLead(view: ContactMemoryView): string {
  if (multiAccountActive(view)) {
    return MULTI_ACCOUNT_LEAD;
  }
  const accounts = memoryAccounts(view.accounts);
  const domain = memoryAccounts(view.domain_accounts);
  const sender = view.sender?.trim() || "this sender";
  if (accounts.length === 0 && domain.length === 0) {
    return `No earlier quotation or order confirmation is known for ${sender}.`;
  }
  if (accounts.length === 0) {
    return `No earlier document for ${sender} itself. Documents for other senders of the same domain:`;
  }
  if (view.unanimous && accounts.length === 1) {
    return `Every earlier document for ${sender} went to one account:`;
  }
  return `Earlier documents for ${sender} went to:`;
}
